import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Hook que gerencia larguras de colunas redimensionáveis interativamente,
 * com persistência em localStorage por dispositivo.
 *
 * Uso:
 *   const { widths, startResize, resetAll, resetColumn } = useColumnResize(
 *     "marcenaria-fila",
 *     [{ key: "cliente" }, { key: "tipo", defaultWidth: "90px" }, ...]
 *   );
 *
 *   <th style={{ width: widths.cliente }}>
 *     Cliente
 *     <ResizeHandle onMouseDown={(e) => startResize("cliente", e)} />
 *   </th>
 *
 * Em modo `?export=1` (iframe oculto do agendador) o hook IGNORA o
 * localStorage e usa apenas os defaults — garante exportações consistentes.
 */

const STORAGE_PREFIX = "pharuerp:col-width:v1:";
const MIN_WIDTH = 40;

function isExportMode() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).has("export");
  } catch {
    return false;
  }
}

function lerLocalStorage(tableId) {
  if (typeof window === "undefined") return {};
  if (isExportMode()) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + tableId);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    // Sanitiza: só aceita números positivos.
    const out = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "number" && Number.isFinite(v) && v >= MIN_WIDTH) {
        out[k] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function salvarLocalStorage(tableId, widths) {
  if (typeof window === "undefined") return;
  if (isExportMode()) return;
  try {
    const payload = {};
    for (const [k, v] of Object.entries(widths)) {
      if (typeof v === "number") payload[k] = Math.round(v);
    }
    if (Object.keys(payload).length === 0) {
      window.localStorage.removeItem(STORAGE_PREFIX + tableId);
    } else {
      window.localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify(payload));
    }
  } catch {
    // ignore quota errors etc.
  }
}

/**
 * @param {string} tableId
 * @param {Array<{ key: string, defaultWidth?: string|number|null }>} columns
 * @param {Object} [opts]
 * @param {(novoMap: Record<string, number>) => void} [opts.onPersist] - Bloco TBL.2:
 *   callback opcional injetado pelo provider de preferencias backend. Quando
 *   provido, SUBSTITUI o save em localStorage (o provider cuida de localStorage
 *   como fallback). Quando ausente, mantem comportamento legado (localStorage
 *   direto) para tabelas que ainda nao migraram.
 * @param {Record<string, number>} [opts.widthsIniciais] - Bloco TBL.2:
 *   larguras pre-carregadas do backend. Quando provido, substitui o load
 *   inicial via localStorage.
 */
export function useColumnResize(tableId, columns = [], opts = {}) {
  const { onPersist, widthsIniciais } = opts;
  // Map<columnKey, number> (apenas larguras customizadas — defaults vêm das cols)
  const [widths, setWidths] = useState(() => {
    if (widthsIniciais && typeof widthsIniciais === "object") {
      // Sanitiza igual ao lerLocalStorage.
      const out = {};
      for (const [k, v] of Object.entries(widthsIniciais)) {
        if (typeof v === "number" && Number.isFinite(v) && v >= MIN_WIDTH) {
          out[k] = v;
        }
      }
      return out;
    }
    return tableId ? lerLocalStorage(tableId) : {};
  });
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const dragRef = useRef(null);

  // Reage a troca de tableId (raro mas defensivo).
  // Tambem reage a widthsIniciais mudar (provider carregou backend).
  useEffect(() => {
    if (!tableId) return;
    if (widthsIniciais && typeof widthsIniciais === "object") {
      const out = {};
      for (const [k, v] of Object.entries(widthsIniciais)) {
        if (typeof v === "number" && Number.isFinite(v) && v >= MIN_WIDTH) {
          out[k] = v;
        }
      }
      setWidths(out);
    } else {
      setWidths(lerLocalStorage(tableId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, widthsIniciais ? JSON.stringify(widthsIniciais) : null]);

  const persist = useCallback(
    (next) => {
      if (!tableId) return;
      // Bloco TBL.2: se onPersist provido, ele eh a fonte de verdade
      // (provider grava no backend + espelha em localStorage como fallback).
      // Sem onPersist: comportamento legado.
      if (typeof onPersist === "function") {
        try {
          onPersist(next);
        } catch {
          /* nunca quebrar UX por causa de save */
        }
      } else {
        salvarLocalStorage(tableId, next);
      }
    },
    [tableId, onPersist]
  );

  /**
   * Inicia o drag de uma coluna. Captura pointer + listeners no document
   * para que o drag continue mesmo quando o cursor sai da célula.
   */
  const startResize = useCallback(
    (columnKey, event) => {
      if (!columnKey) return;
      // Touch em mobile — UX confusa, desabilita.
      if (event.pointerType === "touch") return;

      const th = event.currentTarget.closest("th");
      const larguraAtual = th
        ? th.getBoundingClientRect().width
        : widthsRef.current[columnKey] || 100;

      dragRef.current = {
        columnKey,
        startX: event.clientX,
        startWidth: larguraAtual,
        rafId: null,
        pendingDelta: 0,
      };

      // Captura pointer no próprio elemento para garantir continuidade
      // mesmo fora dele.
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        // ignore
      }

      const onMove = (e) => {
        if (!dragRef.current) return;
        dragRef.current.pendingDelta = e.clientX - dragRef.current.startX;
        if (dragRef.current.rafId == null) {
          dragRef.current.rafId = requestAnimationFrame(() => {
            if (!dragRef.current) return;
            const novo = Math.max(
              MIN_WIDTH,
              dragRef.current.startWidth + dragRef.current.pendingDelta
            );
            setWidths((prev) => ({ ...prev, [columnKey]: novo }));
            dragRef.current.rafId = null;
          });
        }
      };

      const onUp = () => {
        if (!dragRef.current) return;
        if (dragRef.current.rafId != null) {
          cancelAnimationFrame(dragRef.current.rafId);
        }
        dragRef.current = null;
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.removeEventListener("pointercancel", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        persist(widthsRef.current);
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.addEventListener("pointercancel", onUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      event.preventDefault();
    },
    [persist]
  );

  const resetAll = useCallback(() => {
    setWidths({});
    persist({});
  }, [persist]);

  const resetColumn = useCallback(
    (columnKey) => {
      setWidths((prev) => {
        if (!(columnKey in prev)) return prev;
        const next = { ...prev };
        delete next[columnKey];
        persist(next);
        return next;
      });
    },
    [persist]
  );

  // Computed: lista final de widths (customizado || default).
  const efetivos = useMemo(() => {
    const out = {};
    for (const col of columns) {
      const custom = widths[col.key];
      if (typeof custom === "number") {
        out[col.key] = `${custom}px`;
      } else if (col.defaultWidth) {
        out[col.key] = typeof col.defaultWidth === "number"
          ? `${col.defaultWidth}px`
          : col.defaultWidth;
      } else {
        out[col.key] = undefined;
      }
    }
    return out;
  }, [columns, widths]);

  return {
    widths: efetivos,
    rawWidths: widths,
    startResize,
    resetAll,
    resetColumn,
    isCustomized: Object.keys(widths).length > 0,
    MIN_WIDTH,
  };
}
